import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import * as SelectPrimitive from "@radix-ui/react-select";
import { UUID } from "@bitory-gmbh/nidavellir";
import { VDI6022 } from "../../report/types/report";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportResetReferenceNumberMutation,
  useHtzReportShowQuery,
} from "../../report/api/report-api";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { TextInputField } from "@/shared/components/field/input-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { FieldErrors, FieldProps } from "@/shared/components/field/field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { useHtzAirHandlingUnitDataShowQuery } from "../api/ahu-data-api";
import { Separator } from "@/shared/components/ui/separator";
import { ComponentProps } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Check, Minus, X } from "lucide-react";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { CpySelectField } from "@/shared/components/field/cpy-select-field";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";

export function ReportBaseDataCard({
  id,
  disabled,
}: {
  id: UUID;
  disabled?: boolean;
}) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzReportShowQuery({ id });

  const request: PatchRequest = { id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );
  const patchReferenceNumber = useHtzReportPatchMutation();
  const resetReferenceNumber = useHtzReportResetReferenceNumberMutation();

  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: data?.ahuDataId ?? "" },
    { skip: !data || !data.ahuDataId },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlagenbericht")}</CardTitle>
        <CardDescription>{t("Stammdaten zum Bericht")}</CardDescription>
      </CardHeader>
      <CardContent>
        {dataError && <RTKQueryErrorAlert error={dataError} />}
        {dataLoading && <CardContentSkeleton />}
        {data && (
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-6 lg:col-span-3">
              <ReferenceNumberField
                data={data}
                updateCache={updateHtzReportCache}
                patchMutation={patchReferenceNumber}
                resetMutation={resetReferenceNumber}
                disabled={disabled || isImmutable(data)}
              />
            </div>
            <CpySelectField
              label={t("Arbeitsschein")}
              value={toCpyValue(data.workOrderPositionId)}
              placeholder={t("-")}
              warning=""
              destinationId={data.id}
              destinationEntityName={CpyName.HTZReport}
              sourceEntityName={CpyName.HTZWorkOrderPosition}
              sourcesParams={{
                process: { active: true, values: [data.processId!] },
              }}
              fieldProps={{ className: "col-span-6 lg:col-span-3" }}
              disabled
            />
            <TextInputField
              label={t("RLT-Anlage")}
              id="airHandlingUnitId"
              text={ahuData?.designation ?? ""}
              onTextChange={() => {}}
              // Reset by assigning an air handling unit.
              fieldName="airHandlingUnitId"
              fieldProps={{ className: "col-span-2" }}
              disabled
            />
            <TextInputField
              label={t("Bericht Author")}
              id="createdBy"
              text={data.createdBy}
              onTextChange={(createdBy) =>
                set({ ...request, createdBy }, { ...data, createdBy })
              }
              fieldName="createdBy"
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Bericht Datum")}
              fieldName="date"
              nullable
              date={data.date}
              onDateChange={(date) =>
                // null clears the date, an omitted key would leave it.
                set({ ...request, date: date ?? null }, { ...data, date })
              }
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Inspektion durchgeführt von")}
              id="inspectionConductedBy"
              text={data.inspectionConductedBy}
              onTextChange={(inspectionConductedBy) =>
                set(
                  { ...request, inspectionConductedBy },
                  { ...data, inspectionConductedBy },
                )
              }
              fieldName="inspectionConductedBy"
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Begleitendes Personal")}
              id="inspectionConductedByStaff"
              text={data.inspectionConductedByStaff}
              onTextChange={(inspectionConductedByStaff) =>
                set(
                  { ...request, inspectionConductedByStaff },
                  { ...data, inspectionConductedByStaff },
                )
              }
              fieldName="inspectionConductedByStaff"
              fieldProps={{ className: "col-span-4" }}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Letzte Inspektion")}
              fieldName="lastInspectionDate"
              nullable
              date={data.lastInspectionDate}
              onDateChange={(lastInspectionDate) => {
                // null clears the date, an omitted key would leave it.
                set(
                  {
                    ...request,
                    lastInspectionDate: lastInspectionDate ?? null,
                  },
                  { ...data, lastInspectionDate },
                );
              }}
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
              captionLayout="dropdown"
              startMonth={yearSelectionStart}
              endMonth={yearSelectionEnd}
            />
            <DatePickerField
              label={t("Inspektionsdatum")}
              fieldName="inspectionDate"
              nullable
              date={data.inspectionDate}
              onDateChange={(inspectionDate) => {
                // The backend derives the next inspection from the
                // inspection date; mirror it for the optimistic update.
                // null clears the date, an omitted key would leave it.
                set(
                  { ...request, inspectionDate: inspectionDate ?? null },
                  {
                    ...data,
                    inspectionDate,
                    nextInspectionDate: nextInspectionDateAfter(inspectionDate),
                  },
                );
              }}
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Nächste Inspektion")}
              fieldName="nextInspectionDate"
              nullable
              date={data.nextInspectionDate}
              onDateChange={(nextInspectionDate) => {
                // null clears the date, an omitted key would leave it.
                set(
                  {
                    ...request,
                    nextInspectionDate: nextInspectionDate ?? null,
                  },
                  { ...data, nextInspectionDate },
                );
              }}
              fieldProps={{ className: "col-span-2" }}
              disabled={disabled}
              captionLayout="dropdown"
              startMonth={yearSelectionStart}
              endMonth={yearSelectionEnd}
            />
            <Separator className="col-span-6" />
            <VDI6022JudgementField
              label={t("Gesamtbeurteilung")}
              fieldName="vdi6022Judgement"
              description={t(
                "Die Gesamtbeurteilung ergibt sich aus den Ergebnissen der Komponenten: Eine kritische Komponente führt zu „Nicht bestanden“. Sie kann abweichend festgelegt werden, wird aber bei jeder Änderung der Komponenten neu ermittelt.",
              )}
              fieldProps={{ className: "col-span-6" }}
              value={data.vdi6022Judgement}
              onValueChange={(vdi6022Judgement) =>
                set(
                  {
                    ...request,
                    vdi6022Judgement: vdi6022Judgement as VDI6022,
                  },
                  { ...data, vdi6022Judgement: vdi6022Judgement as VDI6022 },
                )
              }
              disabled={disabled}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// The range of the year selection of the inspection dates.
const yearSelectionStart = new Date(1950, 0);
const yearSelectionEnd = new Date(new Date().getFullYear() + 10, 11);

// nextInspectionDateAfter mirrors the backend: the standard inspection
// interval is three years.
function nextInspectionDateAfter(
  inspectionDate: string | undefined,
): string | undefined {
  if (!inspectionDate) return undefined;
  const next = new Date(inspectionDate);
  next.setFullYear(next.getFullYear() + 3);
  return next.toISOString();
}

function VDI6022JudgementField({
  label,
  description,
  errors: ownErrors,
  fieldName,
  fieldProps,
  onValueChange,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & FieldProps) {
  const { errors, invalid, reset } = useFieldErrors(fieldName, ownErrors);

  return (
    <Field data-invalid={invalid || undefined} {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <VDI6022JudgementSelect
        {...props}
        onValueChange={(value) => {
          reset();
          onValueChange?.(value);
        }}
        invalid={invalid}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

function VDI6022JudgementSelect({
  invalid,
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & { invalid?: boolean }) {
  return (
    <Select {...props}>
      <SelectTrigger className="w-full" aria-invalid={invalid || undefined}>
        <SelectValue placeholder={t("Ergebnis auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={VDI6022.Zero}>
            <Minus className="mr-2" />
            <span>{t("Noch nicht beurteilt")}</span>
          </SelectItem>
          <SelectItem value={VDI6022.Passed}>
            <Check className="text-primary mr-2 font-bold" />
            <span className="text-primary font-bold">{t(VDI6022.Passed)}</span>
          </SelectItem>
          <SelectItem value={VDI6022.Failed}>
            <X className="text-destructive mr-2 font-bold" />
            <span className="text-destructive font-bold">
              {t(VDI6022.Failed)}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// toCpyValue maps an unset reference to no selection, so the select shows
// its placeholder.
function toCpyValue(id: string | null | undefined): string | undefined {
  return !id || isZeroUUID(id) ? undefined : id;
}
